import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  useTheme,
  Card,
  Avatar,
  ActivityIndicator,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../store';
import { refreshUser } from '../store/slices/authSlice';
import {
  fetchUserProfile,
  updateUserProfile,
  selectUserProfile,
  selectUserLoading,
  selectUserUpdating,
} from '../store/slices/userSlice';
import Toast from 'react-native-toast-message';
import { validateProfileForm } from '../utils/profileValidation';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { logger } from '../utils/logger';

interface EditProfileFormData {
  first_name: string;
  last_name: string;
  display_name: string;
  bio: string;
  location: string;
  website: string;
  instagram: string;
  twitter: string;
  youtube: string;
  tiktok: string;
}

export function EditProfileScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector(state => state.auth);
  const profile = useAppSelector(selectUserProfile);
  const isLoading = useAppSelector(selectUserLoading);
  const isUpdating = useAppSelector(selectUserUpdating);

  const [formData, setFormData] = useState<EditProfileFormData>({
    first_name: '',
    last_name: '',
    display_name: '',
    bio: '',
    location: '',
    website: '',
    instagram: '',
    twitter: '',
    youtube: '',
    tiktok: '',
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<Partial<EditProfileFormData>>({});

  // Refs to track component lifecycle and prevent duplicate operations
  const hasLoadedData = useRef(false);
  const hasInitializedForm = useRef(false);

  // Effect 1: Load data once on mount
  useEffect(() => {
    const loadData = async () => {
      if (hasLoadedData.current) {
        return;
      }

      try {
        await dispatch(refreshUser());

        if (user?.id) {
          await dispatch(fetchUserProfile(user.id));
        }

        hasLoadedData.current = true;
      } catch (error) {
        logger.error('Failed to load user data:', error);
        hasLoadedData.current = true;
      }
    };

    if (user?.id || !hasLoadedData.current) {
      loadData();
    }
  }, [dispatch, user?.id]);

  // Effect 2: Initialize form when profile data is available
  useEffect(() => {
    const hasData = profile || user;
    const shouldInitialize = hasData && !hasInitializedForm.current;

    if (shouldInitialize) {
      const initialData = {
        first_name: profile?.first_name || user?.first_name || '',
        last_name: profile?.last_name || user?.last_name || '',
        display_name: profile?.display_name || user?.display_name || '',
        bio: profile?.bio || user?.bio || '',
        location: (profile as any)?.location || '',
        website: (profile as any)?.website_url || '',
        instagram: (profile as any)?.instagram_url || '',
        twitter: (profile as any)?.twitter_url || '',
        youtube: (profile as any)?.youtube_url || '',
        tiktok: (profile as any)?.tiktok_url || '',
      };

      setFormData(initialData);
      hasInitializedForm.current = true;
    }
  }, [user, profile]);

  const getInitials = () => {
    const firstName = profile?.first_name || user?.first_name;
    const lastName = profile?.last_name || user?.last_name;
    const username = (profile as any)?.username || user?.username;

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) return firstName[0]?.toUpperCase() || 'C';
    if (username) return username[0]?.toUpperCase() || 'C';
    return 'CE';
  };

  const validateForm = (): boolean => {
    const validationErrors = validateProfileForm(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      Toast.show({
        type: 'error',
        text1: 'Please fix the errors before saving',
      });
      return;
    }

    try {
      const updateData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        display_name: formData.display_name.trim(),
        bio: formData.bio.trim() || null,
        location: formData.location.trim() || null,
        website_url: formData.website.trim() || null,
        instagram_url: formData.instagram.trim() || null,
        twitter_url: formData.twitter.trim() || null,
        youtube_url: formData.youtube.trim() || null,
        tiktok_url: formData.tiktok.trim() || null,
      };

      await dispatch(updateUserProfile(updateData)).unwrap();

      Toast.show({
        type: 'success',
        text1: 'Profile updated successfully',
      });

      setHasChanges(false);
      navigation.goBack();
    } catch (error: any) {
      logger.error('Failed to update profile:', error);
      Toast.show({
        type: 'error',
        text1: 'Failed to update profile',
        text2: error?.message || String(error),
      });
    }
  }, [formData, dispatch, navigation]);

  // Set header with save button
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          mode='text'
          onPress={handleSave}
          disabled={!hasChanges || isUpdating}
          loading={isUpdating}
          labelStyle={{
            fontSize: 16,
            fontWeight: '600',
            color:
              hasChanges && !isUpdating
                ? theme.colors.primary
                : theme.colors.onSurfaceDisabled,
          }}
        >
          Save
        </Button>
      ),
    });
  }, [navigation, hasChanges, isUpdating, theme, handleSave]);

  // ✅ Show loading only while initially loading AND no data available yet
  const showLoading =
    isLoading && !hasInitializedForm.current && !(profile || user);

  if (showLoading) {
    return (
      <ErrorBoundary>
        <SafeAreaView
          style={[
            styles.container,
            { backgroundColor: theme.colors.background },
          ]}
        >
          <View style={styles.loadingContainer}>
            <ActivityIndicator size='large' color={theme.colors.primary} />
            <Text
              variant='bodyMedium'
              style={[
                styles.loadingText,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Loading profile...
            </Text>
          </View>
        </SafeAreaView>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps='handled'
          >
            {/* Avatar Section */}
            <Card
              style={[styles.card, { backgroundColor: theme.colors.surface }]}
            >
              <Card.Content style={styles.avatarSection}>
                <View style={styles.avatarContainer}>
                  {profile?.avatar_url || user?.avatar_url ? (
                    <Avatar.Image
                      size={80}
                      source={{ uri: profile?.avatar_url || user?.avatar_url }}
                      style={styles.avatar}
                    />
                  ) : (
                    <Avatar.Text
                      size={80}
                      label={getInitials()}
                      style={[
                        styles.avatar,
                        { backgroundColor: theme.colors.primaryContainer },
                      ]}
                      color={theme.colors.primary}
                    />
                  )}
                </View>
                <Text
                  variant='bodyMedium'
                  style={[
                    styles.avatarHint,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Tap the avatar to change your profile picture
                </Text>
              </Card.Content>
            </Card>

            {/* Basic Information */}
            <Card
              style={[styles.card, { backgroundColor: theme.colors.surface }]}
            >
              <Card.Content>
                <Text variant='titleMedium' style={styles.sectionTitle}>
                  Basic Information
                </Text>

                <View style={styles.row}>
                  <View style={[styles.inputContainer, styles.halfWidth]}>
                    <TextInput
                      label='First Name *'
                      value={formData.first_name}
                      onChangeText={text => {
                        setFormData(prev => ({ ...prev, first_name: text }));
                        setHasChanges(true);
                        if (errors.first_name)
                          setErrors(prev => ({
                            ...prev,
                            first_name: undefined,
                          }));
                      }}
                      error={!!errors.first_name}
                      style={styles.input}
                      mode='outlined'
                      dense
                    />
                    {errors.first_name && (
                      <Text variant='bodySmall' style={styles.errorText}>
                        {errors.first_name}
                      </Text>
                    )}
                  </View>

                  <View style={[styles.inputContainer, styles.halfWidth]}>
                    <TextInput
                      label='Last Name *'
                      value={formData.last_name}
                      onChangeText={text => {
                        setFormData(prev => ({ ...prev, last_name: text }));
                        setHasChanges(true);
                        if (errors.last_name)
                          setErrors(prev => ({
                            ...prev,
                            last_name: undefined,
                          }));
                      }}
                      error={!!errors.last_name}
                      style={styles.input}
                      mode='outlined'
                      dense
                    />
                    {errors.last_name && (
                      <Text variant='bodySmall' style={styles.errorText}>
                        {errors.last_name}
                      </Text>
                    )}
                  </View>
                </View>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='Display Name *'
                    value={formData.display_name}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, display_name: text }));
                      setHasChanges(true);
                      if (errors.display_name)
                        setErrors(prev => ({
                          ...prev,
                          display_name: undefined,
                        }));
                    }}
                    error={!!errors.display_name}
                    style={styles.input}
                    mode='outlined'
                    dense
                    maxLength={30}
                  />
                  {errors.display_name && (
                    <Text variant='bodySmall' style={styles.errorText}>
                      {errors.display_name}
                    </Text>
                  )}
                </View>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='Bio'
                    value={formData.bio}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, bio: text }));
                      setHasChanges(true);
                      if (errors.bio)
                        setErrors(prev => ({ ...prev, bio: undefined }));
                    }}
                    error={!!errors.bio}
                    style={styles.input}
                    mode='outlined'
                    multiline
                    numberOfLines={3}
                    maxLength={500}
                    right={
                      <TextInput.Affix text={`${formData.bio.length}/500`} />
                    }
                  />
                  {errors.bio && (
                    <Text variant='bodySmall' style={styles.errorText}>
                      {errors.bio}
                    </Text>
                  )}
                </View>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='Location'
                    value={formData.location}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, location: text }));
                      setHasChanges(true);
                    }}
                    style={styles.input}
                    mode='outlined'
                    dense
                    placeholder='e.g., San Francisco, CA'
                  />
                </View>
              </Card.Content>
            </Card>

            {/* Website */}
            <Card
              style={[styles.card, { backgroundColor: theme.colors.surface }]}
            >
              <Card.Content>
                <Text variant='titleMedium' style={styles.sectionTitle}>
                  Website
                </Text>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='Personal Website'
                    value={formData.website}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, website: text }));
                      setHasChanges(true);
                      if (errors.website)
                        setErrors(prev => ({ ...prev, website: undefined }));
                    }}
                    error={!!errors.website}
                    style={styles.input}
                    mode='outlined'
                    dense
                    placeholder='https://yourwebsite.com'
                    keyboardType='url'
                    autoCapitalize='none'
                  />
                  {errors.website && (
                    <Text variant='bodySmall' style={styles.errorText}>
                      {errors.website}
                    </Text>
                  )}
                </View>
              </Card.Content>
            </Card>

            {/* Social Media */}
            <Card
              style={[styles.card, { backgroundColor: theme.colors.surface }]}
            >
              <Card.Content>
                <Text variant='titleMedium' style={styles.sectionTitle}>
                  Social Media
                </Text>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='Instagram'
                    value={formData.instagram}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, instagram: text }));
                      setHasChanges(true);
                      if (errors.instagram)
                        setErrors(prev => ({ ...prev, instagram: undefined }));
                    }}
                    error={!!errors.instagram}
                    style={styles.input}
                    mode='outlined'
                    dense
                    placeholder='@username or username'
                    autoCapitalize='none'
                  />
                  {errors.instagram && (
                    <Text variant='bodySmall' style={styles.errorText}>
                      {errors.instagram}
                    </Text>
                  )}
                </View>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='Twitter'
                    value={formData.twitter}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, twitter: text }));
                      setHasChanges(true);
                      if (errors.twitter)
                        setErrors(prev => ({ ...prev, twitter: undefined }));
                    }}
                    error={!!errors.twitter}
                    style={styles.input}
                    mode='outlined'
                    dense
                    placeholder='@username or username'
                    autoCapitalize='none'
                  />
                  {errors.twitter && (
                    <Text variant='bodySmall' style={styles.errorText}>
                      {errors.twitter}
                    </Text>
                  )}
                </View>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='YouTube'
                    value={formData.youtube}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, youtube: text }));
                      setHasChanges(true);
                      if (errors.youtube)
                        setErrors(prev => ({ ...prev, youtube: undefined }));
                    }}
                    error={!!errors.youtube}
                    style={styles.input}
                    mode='outlined'
                    dense
                    placeholder='Channel URL'
                    keyboardType='url'
                    autoCapitalize='none'
                  />
                  {errors.youtube && (
                    <Text variant='bodySmall' style={styles.errorText}>
                      {errors.youtube}
                    </Text>
                  )}
                </View>

                <View style={styles.inputContainer}>
                  <TextInput
                    label='TikTok'
                    value={formData.tiktok}
                    onChangeText={text => {
                      setFormData(prev => ({ ...prev, tiktok: text }));
                      setHasChanges(true);
                      if (errors.tiktok)
                        setErrors(prev => ({ ...prev, tiktok: undefined }));
                    }}
                    error={!!errors.tiktok}
                    style={styles.input}
                    mode='outlined'
                    dense
                    placeholder='@username or username'
                    autoCapitalize='none'
                  />
                  {errors.tiktok && (
                    <Text variant='bodySmall' style={styles.errorText}>
                      {errors.tiktok}
                    </Text>
                  )}
                </View>
              </Card.Content>
            </Card>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  keyboardAvoidingView: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 32 },
  card: { marginBottom: 16, elevation: 0, borderRadius: 16 },
  avatarSection: { alignItems: 'center', paddingVertical: 24 },
  avatarContainer: { position: 'relative', marginBottom: 12 },
  avatar: { marginBottom: 8 },
  avatarHint: { textAlign: 'center', opacity: 0.8 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 16 },
  sectionTitle: { marginBottom: 16, fontWeight: '700', letterSpacing: -0.3 },
  row: { flexDirection: 'row', gap: 12 },
  inputContainer: { marginBottom: 16 },
  halfWidth: { flex: 1 },
  input: { backgroundColor: 'transparent' },
  errorText: { color: '#d32f2f', marginTop: 4, marginLeft: 12 },
});
